import type { BudgetView, CreditCard } from '@financial/shared';

/** Parte del margen que se conserva ante variaciones e imprevistos. */
export const PURCHASE_SAFETY_BUFFER_RATIO = 0.5;

/**
 * Las MSI son deuda futura aunque hoy el banco tenga línea disponible. Este es
 * un guardrail deliberadamente conservador: no deja que la suma de
 * mensualidades nuevas y existentes rebase 10% del ingreso estable.
 */
export const MAX_MSI_SHARE_OF_STABLE_INCOME = 0.1;

interface BudgetBreakdownWithStableIncome {
  /** Campo opcional mientras los datos históricos se actualizan. */
  stableIncome?: number;
}

export interface PurchasePolicyOptions {
  /**
   * Suma real de mensualidades MSI vigentes. En la vista de tarjeta llega de
   * `/credit-card/installments`; fuera de ella el presupuesto ya reserva las
   * mensualidades de compras previas.
   */
  currentMonthlyMsi?: number;
  /** Simulación de margen posterior a una edición aún no guardada. */
  monthlyMargin?: number;
}

/**
 * Capacidad prudente para una compra nueva. El límite bancario es una segunda
 * barrera; el flujo mensual es siempre la primera.
 */
export interface PurchasePolicy {
  /** Ingreso recurrente/configurado; no incorpora bonos planeados. */
  stableIncome: number;
  /** Compromisos distintos de tarjeta que ya aparta el presupuesto. */
  fixedCommitments: number;
  monthlyMargin: number;
  protectedMargin: number;
  /** Tope de contado que cuida el flujo, no la línea bancaria. */
  cashCap: number;
  /** Precio total prudente, incluso cuando la compra se pagará a meses. */
  purchasePriceCap: number;
  /** Mensualidades MSI ya comprometidas. */
  currentMonthlyMsi: number;
  /** Máximo total de MSI recomendado para el ingreso estable. */
  monthlyMsiCeiling: number;
  /** Espacio restante para una MSI nueva, después de flujo y guardrail. */
  newMonthlyMsiCapacity: number;
  /** Alias de compatibilidad para consumidores existentes. */
  monthlyInstallmentCap: number;
  /** Crédito del banco: sólo sirve para anticipar rechazo, no es un presupuesto. */
  availableCredit: number;
  committedSavings: number;
  savingsTarget: number;
}

export function getPurchasePolicy(
  card: CreditCard,
  budget: BudgetView,
  options: PurchasePolicyOptions = {},
): PurchasePolicy {
  const breakdown = budget.breakdown as BudgetView['breakdown'] & BudgetBreakdownWithStableIncome;
  // Al no tener aún ingreso estable explícito en datos antiguos, usar el
  // ingreso mensual vigente mantiene el cálculo disponible sin inventar un
  // bono como capacidad de deuda.
  const stableIncome = Math.max(0, breakdown.stableIncome ?? breakdown.monthlyIncome);
  const fixedCommitments = Math.max(
    0,
    breakdown.recurringExpenses +
      breakdown.loanPayments +
      breakdown.plannedExpenses +
      breakdown.savingsTarget,
  );
  // Fuera de la pantalla de tarjeta el presupuesto ya separa las MSI previas
  // de las compras nuevas. Así no contamos un cargo ni su pago dos veces.
  const currentMonthlyMsi = Math.max(
    0,
    options.currentMonthlyMsi ?? breakdown.cardPaymentProjected,
  );
  const cashflowAfterCommitments = Math.max(
    0,
    stableIncome - fixedCommitments - currentMonthlyMsi - budget.variableSpent,
  );
  // `availableFree` es el restante del límite mensual tras el gasto variable
  // registrado en cuenta y tarjeta; no es el saldo de esas cuentas. Tomar el
  // menor de ambos evita dos permisos distintos para gastar.
  const simulatedMargin = options.monthlyMargin ?? budget.availableFree;
  const safeMargin = Math.max(0, Math.min(simulatedMargin, cashflowAfterCommitments));
  const protectedMargin = safeMargin * PURCHASE_SAFETY_BUFFER_RATIO;
  const cashCap = Math.max(0, safeMargin - protectedMargin);
  const monthlyMsiCeiling = stableIncome * MAX_MSI_SHARE_OF_STABLE_INCOME;
  const newMonthlyMsiCapacity = Math.max(
    0,
    Math.min(cashCap, monthlyMsiCeiling - currentMonthlyMsi),
  );
  const availableCredit = Math.max(0, card.creditLimit - card.balance);

  return {
    stableIncome,
    fixedCommitments,
    monthlyMargin: safeMargin,
    protectedMargin,
    cashCap,
    purchasePriceCap: cashCap,
    currentMonthlyMsi,
    monthlyMsiCeiling,
    newMonthlyMsiCapacity,
    monthlyInstallmentCap: newMonthlyMsiCapacity,
    availableCredit,
    committedSavings: budget.breakdown.committedSavings,
    savingsTarget: budget.breakdown.savingsTarget,
  };
}

/** Margen que se puede destinar a una meta sin consumir el colchón operativo. */
export function safeExtraSavings(monthlyMargin: number): number {
  return Math.max(0, monthlyMargin) * (1 - PURCHASE_SAFETY_BUFFER_RATIO);
}
